import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { withApiState } from '@/utils/withApiState'

interface NotificationAuthor {
  uuid: string
  firstName: string | null
  lastName: string | null
}

interface Notification {
  uuid: string
  type: string
  entityType: string
  entityId: string
  title: string
  description: string
  payload: any
  createdAt: string
  authorUuid: string | null
  author: NotificationAuthor | null
}

export interface UserNotification {
  notificationUuid: string
  userUuid: string
  status: 'NEW' | 'READ' | 'ARCHIVED'
  readAt: string | null
  archivedAt: string | null
  notification: Notification
}

const UNREAD_POLL_INTERVAL_MS = 60 * 1000

export const useUserNotificationStore = defineStore('userNotification', () => {
  const notifications = ref<UserNotification[]>([])
  const total = ref(0)
  const limit = ref(20)
  const offset = ref(0)
  const unreadCount = ref(0)
  const isLoading = ref(false)
  let unreadPollTimer: ReturnType<typeof setInterval> | null = null

  function startUnreadCountPolling() {
    if (unreadPollTimer) return
    unreadPollTimer = setInterval(() => fetchUnreadCount(), UNREAD_POLL_INTERVAL_MS)
  }

  function stopUnreadCountPolling() {
    if (unreadPollTimer) {
      clearInterval(unreadPollTimer)
      unreadPollTimer = null
    }
  }

  const fetchNotifications = async (params: { status?: string, limit?: number, offset?: number, includeArchived?: boolean } = {}) => {
    const suiteStore = useSuiteStore()
    await withApiState(
      async () => {
        const queryParams = new URLSearchParams()
        if (params.status) queryParams.append('status', params.status)
        if (params.limit) queryParams.append('limit', params.limit.toString())
        if (params.offset !== undefined) queryParams.append('offset', params.offset.toString())
        if (params.includeArchived) queryParams.append('includeArchived', 'true')

        const response = await suiteStore.suiteFetch(`user-notification?${queryParams.toString()}`, {
          credentials: 'include',
        })
        if (response && typeof response !== 'boolean' && response.ok) {
          const result = await response.json()
          notifications.value = result.data.items
          total.value = result.data.total
          limit.value = result.data.limit
          offset.value = result.data.offset
        }
      },
      { loading: isLoading, logLabel: 'Erreur lors de la récupération des notifications:' },
    )
  }

  const fetchUnreadCount = async () => {
    const suiteStore = useSuiteStore()
    await withApiState(
      async () => {
        const response = await suiteStore.suiteFetch('user-notification/unread-count', {
          credentials: 'include',
        })
        if (response && typeof response !== 'boolean' && response.ok) {
          const result = await response.json()
          unreadCount.value = result.data.count
        }
      },
      { logLabel: 'Erreur lors de la récupération du compteur d\'invendus:' },
    )
  }

  const markAsRead = async (uuid: string) => {
    const suiteStore = useSuiteStore()
    try {
      const response = await suiteStore.suiteFetch(`user-notification/${uuid}/read`, {
        method: 'PATCH',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const index = notifications.value.findIndex(n => n.notificationUuid === uuid)
        if (index !== -1) {
          notifications.value[index].status = 'READ'
          notifications.value[index].readAt = new Date().toISOString()
        }
        await fetchUnreadCount()
        return true
      }
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error)
    }
    return false
  }

  const markAllAsRead = async () => {
    const suiteStore = useSuiteStore()
    try {
      const response = await suiteStore.suiteFetch('user-notification/read-all', {
        method: 'PATCH',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        notifications.value.forEach(n => {
          if (n.status === 'NEW') {
            n.status = 'READ'
            n.readAt = new Date().toISOString()
          }
        })
        unreadCount.value = 0
        return true
      }
    } catch (error) {
      console.error('Erreur lors du marquage de toutes comme lues:', error)
    }
    return false
  }

  const archiveNotification = async (uuid: string) => {
    const suiteStore = useSuiteStore()
    try {
      const response = await suiteStore.suiteFetch(`user-notification/${uuid}/archive`, {
        method: 'PATCH',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const index = notifications.value.findIndex(n => n.notificationUuid === uuid)
        if (index !== -1) {
          notifications.value.splice(index, 1)
          total.value--
        }
        await fetchUnreadCount()
        return true
      }
    } catch (error) {
      console.error('Erreur lors de l\'archivage de la notification:', error)
    }
    return false
  }

  const sendAdminNotification = async (payload: {
    title: string
    description: string
    userUuids?: string[]
    permissions?: string[]
    bu?: string | null
  }) => {
    const suiteStore = useSuiteStore()
    try {
      const response = await suiteStore.suiteFetch('user-notification/send-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const result = await response.json()
        return result.data
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification administrative:', error)
    }
    return null
  }

  return {
    notifications,
    total,
    limit,
    offset,
    unreadCount,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    sendAdminNotification,
    startUnreadCountPolling,
    stopUnreadCountPolling,
  }
})
